import React, { useState, useReducer, useMemo, useEffect, useRef } from 'react';
import { Slate } from 'slate-react';
import { Editor, Transforms } from 'slate';
import { useQuery, useMutation } from 'react-query'
import { useTranslation } from 'react-i18next'
import produce from 'immer'
import { getEditorInstance } from './Canvas/TratoFormat';
import { useForm, FormProvider } from 'react-hook-form'; 
import { useVariables } from './Canvas/TratoFormat/Elements';
import { migrateElements } from './Canvas/TratoFormat';
import { fetchContract, contractAction } from 'api/actions/contracts'
import { fetchTemplate, templateAction } from 'api/actions/templates'
import { fetcher } from 'api/client'
import { useConstant } from 'hooks/useConstant'
import { useProfileState } from 'context/profile'
import { useChannel, useEvent } from '@harelpls/use-pusher';

import useEditorActions from './hooks/useEditorActions'
import useWhoami from 'hooks/useWhoami'
import { truncate, getMongoObjectId } from 'utils'
import bootbox from 'components/Modals/Bootbox';



const EditorStateContext = React.createContext();
const EditorDispatchContext = React.createContext();

const SET_DOCUMENT = 'SET_DOCUMENT';
export const UPDATE_DOCUMENT = 'UPDATE_DOCUMENT';
export const UPDATE_ELEMENTS = 'UPDATE_ELEMENTS';
export const SET_PARTICIPANTS = 'SET_PARTICIPANTS';
export const REMOVE_SIGNATURE = 'REMOVE_SIGNATURE';
export const SET_CURRENT_ELEMENT = 'SET_CURRENT_ELEMENT';
export const UPDATE_VARIABLE = 'UPDATE_VARIABLE';
export const UPDATE_SIGNATURE = 'UPDATE_SIGNATURE';
export const ADD_SIGNATURE = 'ADD_SIGNATURE';
export const ADD_PARTICIPANT = 'ADD_PARTICIPANT';
export const UPDATE_PARTICIPANT = 'UPDATE_PARTICIPANT';
export const REMOVE_PARTICIPANT = 'REMOVE_PARTICIPANT';
export const SET_TOTAL_PAGES = 'SET_TOTAL_PAGES';
export const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE';
export const PREV_PAGE = 'PREV_PAGE';
export const NEXT_PAGE = 'NEXT_PAGE';
export const SET_CURRENT_SIDEBAR_TAB = 'SET_CURRENT_SIDEBAR_TAB';
export const SET_INIT_WORKFLOW_IN_PROGRESS = 'SET_INIT_WORKFLOW_IN_PROGRESS';
export const SET_PREVIEW_ONLY = 'SET_PREVIEW_ONLY';
export const CHANGE_WORD = 'CHANGE_WORD';
export const SET_IS_FETCHING = 'SET_IS_FETCHING';
export const SET_IS_MIGRATING = 'SET_IS_MIGRATING';
export const ADD_VARIABLE = 'ADD_VARIABLE';
export const REMOVE_VARIABLE = 'REMOVE_VARIABLE';
export const CHANGE_ELEMENT_VERSION = 'CHANGE_ELEMENT_VERSION';
export const SET_SIDEBAR_OPEN = 'SET_SIDEBAR_OPEN';


const reducer = produce((draft, action) => {
  switch (action.type) {
    case SET_DOCUMENT:
      draft.document = action.document
      draft.originalDocument = action.document
      draft.elements = action.document.elements;
      draft.documentType = determineDocumentType(action.document);
      draft.isContentLocked = action.document.locked;

      return;
    case UPDATE_DOCUMENT:
      draft.document = {
        ...draft.document,
        ...action.document
      }
      draft.documentType = determineDocumentType(draft.document);
      draft.isContentLocked = draft.document.locked;
      return;
    case UPDATE_ELEMENTS:
      draft.elements = action.elements
      return;
    case SET_PARTICIPANTS:
      draft.participants = getParticipantsMap(action.participants);
      return;
    case REMOVE_SIGNATURE:
      if (draft.documentType === 'pdf') {
        const participants = draft.document.participants;
        const participantIdx = participants.findIndex(p => p._id === action.participantId);

        if (participantIdx < 0) return;

        const participant = participants[participantIdx];

        participant.signatures = participant.signatures.filter(s => s._id !== action.signatureId);

      } else {
        const elementIndex = draft.elements.findIndex(el => el._id === action.elementId)
        const element = draft.elements[elementIndex];

        element.participants = element.participants.filter(p => p !== action.participantId);
      }

      return;
    case SET_CURRENT_ELEMENT:
      draft.currentElement = { ...action.element }
      draft.currentElement.editorLocation = action.location

      return;
    case SET_CURRENT_SIDEBAR_TAB:
      draft.currentSidebarTab = action.tab;

      return;
    case UPDATE_SIGNATURE:
      const participants = draft.document.participants;
      const participantIdx = participants.findIndex(p => p._id === action.participantId);

      if (participantIdx < 0) return;

      const signatures = participants[participantIdx].signatures;
      const signatureIdx = signatures.findIndex(s => s._id === action.signature._id);

      if (signatureIdx < 0) return;

      const signature = signatures[signatureIdx]

      signature.top = action.signature.top;
      signature.left = action.signature.left;
      // signature.width = action.signature.width;
      // signature.height = action.signature.height;

      return;

    case ADD_SIGNATURE:
      const participants2 = draft.document.participants;
      const participantIdx2 = participants2.findIndex(p => p._id === action.participantId);

      if (participantIdx2 < 0) return;

      participants2[participantIdx2].signatures.push(action.signature);

      return;

    case ADD_PARTICIPANT:
      draft.document.participants.push(action.participant)
      draft.participants = getParticipantsMap(draft.document.participants);
      return;
    case UPDATE_PARTICIPANT:
      const participantIdx3 = draft.document.participants.findIndex(p => p._id === action.participantId);

      if (participantIdx3 < 0) return;

      draft.document.participants[participantIdx3] = {
        ...draft.document.participants[participantIdx3],
        ...action.participant,
        agendaProperties: {
          ...(draft.document.participants[participantIdx3].agendaProperties || {}),
          ...(action.participant.agendaProperties || {}),
        },
        _id: action.participantId
      }
      draft.participants = getParticipantsMap(draft.document.participants);
      return;
    case REMOVE_PARTICIPANT:
      const deletedPosition = draft.document.participants.findIndex(p => p._id === action.participantId)
      draft.document.participants = draft.document.participants.filter(p => p._id !== action.participantId);

      // reOrder
      if (!Number.isNaN(deletedPosition)) {
        draft.document.participants.forEach((part) => {
            if (part.signOrder > deletedPosition) {
                part.signOrder = part.signOrder - 1;
            }
            if (part.index > deletedPosition) {
                part.index = part.index - 1;
            }
        });
    } 

      draft.participants = getParticipantsMap(draft.document.participants);
      return;
      case ADD_VARIABLE:
        const participantIdx4 = draft.document.participants.findIndex(p => p._id === action.participantId)

        draft.document.participants[participantIdx4].variables.push(action.variable)
  
        draft.participants = getParticipantsMap(draft.document.participants);
        return;
      case UPDATE_VARIABLE:
        const participantIdx5 = draft.document.participants.findIndex(p => p._id === action.participantId);
  
        if (participantIdx5 < 0) return;

        const variableIdx = draft.document.participants[participantIdx5].variables.findIndex(v => v._id === action.variable._id);
        
        if (variableIdx < 0) return;

        let variableToUpdate = draft.document.participants[participantIdx5].variables[variableIdx];

        draft.document.participants[participantIdx5].variables[variableIdx] = {
          ...variableToUpdate,
          ...action.variable,
        }
  
        draft.participants = getParticipantsMap(draft.document.participants);
        return;
      case REMOVE_VARIABLE:
        const participantIdx6 = draft.document.participants.findIndex(p => p._id === action.participantId)
        const participant =  draft.document.participants[participantIdx6];

        draft.document.participants[participantIdx6].variables = participant.variables.filter(v => v._id !== action.variableId);
  
        draft.participants = getParticipantsMap(draft.document.participants);
        return;
    case SET_TOTAL_PAGES:
      draft.totalPages = action.totalPages;

      return;
    case SET_IS_FETCHING:
      draft.isFetching = action.isFetching;

      return;
    case SET_IS_MIGRATING:
      draft.isMigrating = action.isMigrating;

      return;
    case NEXT_PAGE:
      draft.currentPage = Math.min(draft.currentPage + 1, draft.totalPages);

      return;
    case PREV_PAGE:
      draft.currentPage = Math.max(draft.currentPage - 1, 1);

      return;
    case SET_CURRENT_PAGE:
      draft.currentPage = action.page;

      return;
    case SET_INIT_WORKFLOW_IN_PROGRESS:
      draft.isInitWorkflowInProgress = action.isInitWorkflowInProgress;

      return;
    case SET_PREVIEW_ONLY:
      draft.isPreviewOnly = action.isPreviewOnly;

      return;
    case CHANGE_WORD:
      draft.document.wordonline.Version = action.versionId;

      return;
    case SET_SIDEBAR_OPEN:
      draft.isSidebarOpen = action.value;

      return;
    case CHANGE_ELEMENT_VERSION:
      const [firstPath, ...path] = action.elementPath;
      let element = draft.elements[firstPath];
      

      if (path?.length > 0) {
        element = element.children;
        path.map((p, i) => {
            if (path.length === i + 1) {
                element = element[p]
            } else {
                element = element[p].children;
            }
        })
      }
      element.currentLocalVersionId = action.version._id;
      element.currentLocalVersion = action.version;
      element.compareHTML = action.compareHTML
      // element.children = action.version.children;
      break;
    default:
      return draft
  }
}, {})

const initialState = {
  document: {},
  originalDocument: {},
  participants: new Map(),
  currentElement: {},
  currentSidebarTab: {},
  documentType: '',
  currentPage: 1,
  totalPages: 1,
  isContentLocked: false,
  isEditable: false,
  isMigrating: false,
  isInitWorkflowInProgress: false,
  isPreviewOnly: false,
  isFetching: false,
  isSidebarOpen: false,
  elements: [{ type: 'p', children: [{ text: ''}], initialValue: true }],
  variablesState: {},
}

function determineDocumentType(doc) {
  if (doc.originalType === 'pdf' || doc.importedPdf) {
    return 'pdf'
  } else if (doc.originalType === 'wordonline' || doc.importedWord || doc.wordonline) {
    return 'word'
  } else {
    return 'trato'
  }
}

function setFormValues(doc) {
    return {
        ...doc,
        participants: doc.participants?.map((p, i) => ({...p, index: i })) ?? [],
        jurisdiction: doc.jurisdiction ?? 'mx',
        language: doc.language?.toLowerCase?.() ?? 'es',
        type: doc.type?._id || null,
        blockchain: doc.blockchain?.type ?? 'none',
        notificationType: doc.notificationType ?? 'email'
    }
}

function EditorProvider({ mode, children, isPrintMode, documentId }) {
  const {t} = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const editor = useConstant(() => getEditorInstance({}))
  const { profile } = useProfileState();
  const methods = useForm();
  const isSomeoneEditedOpen = useRef(false);
  const { mutate: mutateDoAction } = useMutation(({ documentId, action, ...payload }) => {
    const doAction = mode === 'template' ? templateAction : contractAction;

    return fetcher(doAction(documentId, action, payload))
  }, {
    onSuccess: (data, v) => {
      console.log('Success')
    },
    onError: (error) => {
      console.log({ error })
    }
  })
  const { status, refetch } = useQuery(['document', documentId], () => {
    let action = fetchContract(documentId);

    if (mode === 'template') action = fetchTemplate(documentId);

    return fetcher(action)
  }, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      let actionType = SET_DOCUMENT;

      // if (state.document._id) {
      //   actionType = UPDATE_DOCUMENT;
      // }

      Transforms.deselect(editor);
      dispatch({ type: actionType, document: data })
      dispatch({ type: SET_PARTICIPANTS, participants: data.participants })
    }
  })

  const isParticipantsOk = useMemo(() => state.isTemplate ? true : state.document.participants?.length >= 1 && state.document.participants.every((participant) => participant.label && participant.email), [state.document.participants, state.isTemplate])
  const isConfigOk = useMemo(() => !!(state.document.name && state.document.type), [state.document.name, state.document.type]);

  const channel = useChannel(state.document?._id && mode === 'contract' && `private-${state.document._id}`);
  
  useEvent(channel, 'contractUpdate', (user) => {
      const isSameUser = user._id === profile._id;
      const permitedEvents = ['vote', 'voter-viewed-contract'];
      if (!isSameUser && !isSomeoneEditedOpen.current) { //&& !permitedEvents.includes(user.event)
        isSomeoneEditedOpen.current = true
        bootbox.dialog({
          message: t('editor.action.someone_edited'),
          closeButton: false,
          buttons: {
              reload: {
                  label: 'Recargar',
                  color: 'primary',
                  callback: () => {
                      isSomeoneEditedOpen.current = false
                      Transforms.deselect(editor);
                      refetch()
                  }
              }
          }
        })
      }
      
  });

  const { whoami } = useWhoami(state.document)
  ///TODO: change to casl logic
  const isEditable = useMemo(() => {
    if (state.isMigrating) return false;
    if (mode === 'contract' && status === 'success') {
      return whoami.isOwner() && ['', 'PRE_APPROVED'].includes(state.document.status);
    } else if (status === 'success') {
      return whoami.isOwner() || whoami.isPartnerClient() || !whoami.isWatcher();
    }
  }, [state.document.status, state.isMigrating, status, mode, whoami])

  const variablesState = useVariables(state.document.participants, {
    maxSuggestions: 10,
    trigger: '@',
    jurisdiction: state.document?.jurisdiction ?? 'mx',
    isTestament: state.document?.isTestament
  });


  const handleEditorChange = (value) => {
    dispatch({ type: UPDATE_ELEMENTS, elements: value })
    // mentionState.onChangeMention(editor)
    variablesState.onChangeVariable(editor)

  }

  useEffect(() => {
    if (!state.elements) {
       Transforms.deselect(editor);
       // or set selection to neutral state:
       // editor.selection = { anchor: { path: [0,0], offset:0 }, focus: { path: [0,0], offset: 0 } }
    }
  }, [state.elements]);

  console.log({ state, selection: editor.selection })
  // console.log({el:  state.currentElement, path: state.currentElement.editorLocation })

  return (
    <FormProvider {...methods}>
      <Slate editor={editor} value={state.elements} onChange={handleEditorChange}>
          <EditorStateContext.Provider value={{ ...state, mode, status, variablesState, isEditable, isPrintMode, isParticipantsOk, isConfigOk }}>
            <EditorDispatchContext.Provider value={dispatch}>
              {children}
            </EditorDispatchContext.Provider>
          </EditorStateContext.Provider>
      </Slate>
    </FormProvider> 
  )
}


function useEditorState() {
  const context = React.useContext(EditorStateContext)
  if (context === undefined) {
    throw new Error('useEditorState must be used within a EditorProvider')
  }
  return context
}

function useEditorDispatch() {
  const context = React.useContext(EditorDispatchContext)
  if (context === undefined) {
    throw new Error('useEditorDispatch must be used within a EditorProvider')
  }
  return context
}

function useEditor() {
  return [useEditorState(), useEditorDispatch()]
}

export { EditorProvider, useEditor, useEditorState, useEditorDispatch, useEditorActions }

export function getParticipantsMap(participants = []) {
  if (!participants.length) return new Map([])

  return new Map(participants.map((participant, index) => {
    const draftParticipant = { ...participant, index: index + 1 };

    if (!draftParticipant.variables) {
      draftParticipant.variables = [];
    }

    draftParticipant.variables = new Map(draftParticipant.variables.map(variable => {
      return [variable._id, variable]
    }))

    const signaturesByPageObj = draftParticipant?.signatures?.reduce((obj, signature) => {
      if (!obj[signature.page]) obj[signature.page] = [];
      obj[signature.page].push(signature)
      return obj
    }, {}) ?? {}

    draftParticipant.signatures = new Map(draftParticipant.signatures?.map(signature => {
      return [signature._id, signature]
    }) ?? [])

    draftParticipant.signaturesByPage = new Map(Object.keys(signaturesByPageObj).map(signaturePage => {
      return [signaturePage, signaturesByPageObj[signaturePage]]
    }))

    return [participant._id, draftParticipant]
  }))
}